import React, { useRef, useState } from "react";
import { Tooltip, Dropdown, Menu, message, Form, Input, Modal, Spin } from "antd";
import importExportIcon from "../../assets/import-export.svg";
import classes from "../Toolbar/Toolbar.module.css";
import "../modalStyles.css";
import { useDispatch, useSelector } from "react-redux";
import { saveAs } from "file-saver";
import { importPresentation, slideAction, saveMirrorToCloudinary } from "../../store/slideSlice";
import { useParams } from "react-router-dom/cjs/react-router-dom";
import { slideAPI } from "../../store/api";
import parse from "pptx-parser";
import { v4 } from "uuid";
import { exportToPPTX, getExportPreview, exportToPDF } from "../../utils/pptxExport";
import { getSnapshot } from "../../utils/utils";
import { uploadToCloudinary } from "../../utils/cloudinary";

const ImportExportMenu = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const slides = useSelector((state) => state.slide.slides);
  const mirrorSlides = useSelector((state) => state.slide.mirrorSlides);
  const documentInformation = useSelector((state) => state.slide.documentInformation);
  const masterSlideId = useSelector((state) => state.slide.masterSlideId);
  let currentSlideId = useSelector((state) => state.slide.currentSlideId);
  const [fileName, setFileName] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [exportType, setExportType] = useState("slide");
  const [isExporting, setIsExporting] = useState(false);
  const [form] = Form.useForm();

  const handleMenuClick = (data) => {
    const key = data.key;
    if (key === "import") {
      fileInputRef.current.click();
    } else if (key === "export.slide") {
      const fileName = documentInformation?.name || "Untitled document";
      form.setFieldsValue({ fileName: fileName });
      setFileName(fileName);
      setExportType("slide");
      setIsModalVisible(true);
    } else if (key === "export.pptx") {
      const fileName = documentInformation?.name || "Untitled document";
      form.setFieldsValue({ fileName: fileName });
      setFileName(fileName);
      setExportType("pptx");
      setIsModalVisible(true);
    } else if (key === "export.pdf") {
      const fileName = documentInformation?.name || "Untitled document";
      form.setFieldsValue({ fileName: fileName });
      setFileName(fileName);
      setExportType("pdf");
      setIsModalVisible(true);
    }
  };

  const handleExport = async () => {
    form.validateFields()
      .then(async () => {
        setIsExporting(true);
        try {
          if (exportType === "pptx") {
            // Export as PowerPoint
            // Validate slides exist
            if (!slides || Object.keys(slides).length === 0) {
              message.error("No slides to export.");
              setIsExporting(false);
              return;
            }

            // Get export preview info
            const preview = getExportPreview(slides);
            console.log(`Exporting presentation: ${preview.slideCount} slides, ${preview.elements} elements`);

            // Show loading message for large presentations
            if (preview.slideCount > 10) {
              message.loading({
                content: `Exporting ${preview.slideCount} slides...`,
                key: 'pptxExport',
                duration: 0
              });
            }

            const result = await exportToPPTX(slides, fileName, {
              author: documentInformation?.author || "MileSlide",
              company: documentInformation?.company || "MileSlide",
              subject: documentInformation?.description || "Presentation"
            });

            // Hide loading message
            message.destroy('pptxExport');

            if (result.success) {
              message.success(result.message || "PowerPoint file exported successfully!");
              setIsModalVisible(false);
            } else {
              message.error(result.message || "Failed to export PowerPoint file.");
            }
          } else if (exportType === "pdf") {
            // Export as PDF
            // Validate slides exist
            if (!slides || Object.keys(slides).length === 0) {
              message.error("No slides to export.");
              setIsExporting(false);
              return;
            }

            // Get export preview info
            const preview = getExportPreview(slides);

            // Show loading message for large presentations
            if (preview.slideCount > 5) {
              message.loading({
                content: `Generating PDF with ${preview.slideCount} slides...`,
                key: 'pdfExport',
                duration: 0
              });
            }

            const result = await exportToPDF(slides, mirrorSlides, fileName, {
              author: documentInformation?.author || "MileSlide",
              company: documentInformation?.company || "MileSlide",
              subject: documentInformation?.description || "Presentation"
            });

            // Hide loading message
            message.destroy('pdfExport');

            if (result.success) {
              message.success(result.message || "PDF file exported successfully!");
              setIsModalVisible(false);
            } else {
              message.error(result.message || "Failed to export PDF file.");
            }
          } else {
            // Export as .slide (enhanced format)
            // Get slides in the correct order (by slide ID number)
            const orderedSlideIds = Object.keys(slides).sort((a, b) => {
              const aNum = parseInt(a.replace('slide', '')) || 0;
              const bNum = parseInt(b.replace('slide', '')) || 0;
              return aNum - bNum;
            });

            const orderedSlides = {};
            orderedSlideIds.forEach(slideId => {
              orderedSlides[slideId] = slides[slideId];
            });

            const content = {
              version: "1.0.0", // Version for future compatibility
              exportDate: new Date().toISOString(),
              presentation: {
                name: documentInformation?.name || "Untitled document",
                description: documentInformation?.description || "",
                createdAt: documentInformation?.createdAt,
                updatedAt: documentInformation?.updatedAt,
              },
              slides: orderedSlides,
              mirrorSlides: mirrorSlides,
              masterSlideId: masterSlideId || "",
              metadata: {
                slideCount: Object.keys(slides).length,
                currentSlideId: currentSlideId,
                slideOrder: orderedSlideIds
              }
            };
            const blob = new Blob([JSON.stringify(content, null, 2)], {
              type: "application/json",
            });
            saveAs(blob, `${fileName}.slide`);
            message.success("Presentation exported successfully!");
            setIsModalVisible(false);
          }
        } catch (error) {
          console.error("Error exporting slides:", error);
          message.destroy('pptxExport');
          message.error(`Failed to export file: ${error.message || "Unknown error"}`);
        } finally {
          setIsExporting(false);
        }
      })
      .catch((validationError) => {
        console.error("Validation failed:", validationError);
        message.error("Please enter a valid file name.");
        setIsExporting(false);
      });
  };


  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split('.').pop();

    const resetInput = () => { event.target.value = ""; };

    if (!['pptx', 'slide'].includes(fileExtension)) {
      message.error("Invalid file type. Please upload a .slide or .pptx file.");
      return resetInput();
    }

    Modal.confirm({
      title: "Confirm",
      content: "Do you want to overwrite?",
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        try {
          switch (fileExtension) {
            case "pptx": {
              const data = await parse(file);
              await cleanDataAndImportPptxFile(data);
              break;
            }

            case "slide": {
              const text = await file.text();
              const importedData = JSON.parse(text);

              // Support both legacy and new format
              let slidesData, mirrorSlidesData, masterSlideIdData, presentationInfo;

              // Check if it's the new format with version
              if (importedData.version) {
                slidesData = importedData.slides;
                mirrorSlidesData = importedData.mirrorSlides;
                masterSlideIdData = importedData.masterSlideId;
                presentationInfo = importedData.presentation;

                // If slide order is preserved in metadata, use it to maintain order
                if (importedData.metadata?.slideOrder) {
                  const orderedSlides = {};
                  importedData.metadata.slideOrder.forEach(slideId => {
                    if (slidesData[slideId]) {
                      orderedSlides[slideId] = slidesData[slideId];
                    }
                  });
                  // Add any slides that might not be in the order array
                  Object.keys(slidesData).forEach(slideId => {
                    if (!orderedSlides[slideId]) {
                      orderedSlides[slideId] = slidesData[slideId];
                    }
                  });
                  slidesData = orderedSlides;
                }
              } else {
                // Legacy format: direct slides and mirrorSlides
                slidesData = importedData.slides;
                mirrorSlidesData = importedData.mirrorSlides;
              }

              // Update slides and mirror slides
              // Ensure all slides have required properties
              const initializedSlides = {};
              Object.keys(slidesData).forEach(slideId => {
                const slide = slidesData[slideId];
                initializedSlides[slideId] = {
                  slideBackgroundColor: slide.slideBackgroundColor || "#ffffff",
                  textBoxIds: slide.textBoxIds || [],
                  textBox: slide.textBox || {},
                  imageIds: slide.imageIds || [],
                  image: slide.image || {},
                  shapes: slide.shapes || {},
                  speakerNotes: slide.speakerNotes || "",
                  additionalData: slide.additionalData || { isSlideColorChanged: false },
                  ...slide // Preserve all other properties
                };
              });

              // Ensure mirrorSlides are initialized for all slides
              const initializedMirrorSlides = { ...mirrorSlidesData };
              Object.keys(initializedSlides).forEach(slideId => {
                if (!initializedMirrorSlides[slideId]) {
                  initializedMirrorSlides[slideId] = "";
                }
              });

              dispatch(slideAction.updateSlidesData({
                slides: initializedSlides,
                mirrorSlides: initializedMirrorSlides
              }));

              // Update master slide ID if present
              if (masterSlideIdData) {
                dispatch(slideAction.setMasterSlideId(masterSlideIdData));
              }

              // Update document information if available
              if (presentationInfo?.name) {
                dispatch(slideAction.setDocumentInformation({
                  ...documentInformation,
                  name: presentationInfo.name,
                  description: presentationInfo.description,
                }));
              }

              // Save to database (single request)
              const importPayload = {
                presentationId: id,
                slideData: {
                  slides: initializedSlides,
                  mirrorSlides: initializedMirrorSlides,
                },
              };
              await slideAPI.saveImportedDataToDb(importPayload);
              break;
            }

            default:
              message.error("Unsupported file type.");
              return;
          }

          message.success("Slides imported successfully.");
        } catch (error) {
          console.error("Import error:", error);
          message.error("Failed to import slides. Please try again.");
        } finally {
          resetInput();
        }
      },
      onCancel: resetInput,
    });
  };

  const convertBlobUrlToBase64 = async (blobUrl) => {
    if (!blobUrl || !blobUrl.startsWith('blob:')) return blobUrl;
    try {
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting blob URL to base64:', error);
      return null;
    }
  };



  const cleanDataAndImportPptxFile = async (data) => {
    if (data !== undefined) {
      const slides = data.slides;
      const dataStructure = {};
      let pageWidth = 960;
      let pageHeight = 540;
      let percentage = 100;
      for (let i = 0; i < slides.length; i++) {
        const obj = slides[i];
        const pageElements = obj.pageElements;
        const pageFill = obj.pageProperties.pageBackgroundFill.solidFill;
        let slideID = "slide" + i;
        // let slideID = "slide" + v4();
        dataStructure[slideID] = { slideBackgroundColor: pageFill };
        let slideDataStructure = dataStructure[slideID];
        slideDataStructure["textBoxIds"] = [];
        slideDataStructure["imageIds"] = [];
        slideDataStructure["image"] = {};
        slideDataStructure["speakerNotes"] = "";
        slideDataStructure["shapes"] = {};

        for (let j = 0; j < pageElements.length; j++) {
          const elementObj = pageElements[j];
          let boundBox = {},
            position = {},
            textIDs = [],
            textBox = {},
            images = {},
            backgroundColor = "",
            zIndex;

          if (elementObj.order) {
            // Cap base z-index at 50 to leave room for text offset
            const baseZIndex = Math.min(elementObj.order, 50);
            // Images get base z-index, text gets +50 to always render on top
            zIndex = elementObj.shape && elementObj.shape.text !== undefined ? baseZIndex + 50 : baseZIndex;
          } else {
            // Default z-index: images at 10, text at 60
            zIndex = elementObj.shape && elementObj.shape.text !== undefined ? 60 : 10;
          }

          if (elementObj.boundBox) {
            const boundBoxArr = elementObj.boundBox.filter(
              (item) => item !== 0
            );
            //BoundingBoxes(width/height) & Position Logic
            boundBox["width"] = Number(boundBoxArr[0] / pageWidth) * percentage + '%';
            if (boundBoxArr[1] !== undefined) {
              // let height = boundBoxArr[1];
              boundBox["height"] = Number(boundBoxArr[1] / pageHeight) * percentage + '%';
            }
            position["left"] = Number(elementObj.position.x.value / pageWidth) * percentage + '%';
            position["top"] = Number(elementObj.position.y.value / pageHeight) * percentage + '%';
          }

          if (elementObj.fill && elementObj.fill.solidFill) {
            backgroundColor = elementObj.fill.solidFill;
          }

          const shape = elementObj.shape;
          const image = elementObj.image;

          if (shape)
            if (shape.text !== undefined) {
              const textId = "text" + v4();
              slideDataStructure.textBoxIds.push(textId);
              // const textSpansArr = shape.text.paragraphs[0].textSpans;
              // const textWithContent = textSpansArr.filter(
              //   (item) => item.textRun !== undefined
              // );
              const paragraphArr = shape.text.paragraphs;
              const delta = { ops: [] };
              for (let i = 0; i < paragraphArr.length; i++) {
                const textSpansArr = shape.text.paragraphs[i].textSpans;
                const textWithContent = textSpansArr.filter(
                  (item) => item.textRun || item.listMarker
                );

                for (let j = 0; j < textWithContent.length; j++) {
                  const content = (textWithContent[j].textRun && textWithContent[j].textRun.content !== "") ? textWithContent[j].textRun.content : textWithContent[j].listMarker.bullet.glyph;
                  const attributes = textWithContent[j].textRun ? textWithContent[j].textRun.style : null;
                  const bulletAttributes = textWithContent[j].listMarker && textWithContent[j].listMarker.bullet.bulletStyle;
                  // const bullet = textWithContent[j].te;
                  if (attributes) {
                    const styleObj = {};
                    const filteredObject = Object.fromEntries(
                      Object.entries(attributes).filter(([key, val]) => {
                        if (key === "backgroundColor") {
                          if (val !== null) {
                            styleObj["background"] = val;
                          }
                        } else if (key === "foregroundColor") {
                          if (val) {
                            styleObj["color"] = val;
                          }
                        } else if (key === "fontFamily") {
                          if (val) {
                            styleObj["font"] = val;
                          }
                        } else if (key === "bold") {
                          if (val === true) {
                            styleObj["bold"] = val;
                          }
                        } else if (key === "italic") {
                          if (val === true) {
                            styleObj["italic"] = val;
                          }
                        } else if (key === "strikethrough") {
                          if (val !== "noStrike") {
                            styleObj["strike"] = val;
                          }
                        } else if (key === "underline") {
                          if (val === true) {
                            styleObj["underline"] = true;
                          }
                        } else if (key === "link") {
                          if (val !== null) {
                            styleObj["link"] = val;
                          }
                        } else if (key === "fontSize") {
                          if (val) {
                            const { value } = val;
                            let size = Math.round(value);
                            if (size % 2 === 1) {
                              size = size - 1;
                            }
                            styleObj["size"] = `${size}px`;
                          }
                          // if (value < 14) {
                          //   styleObj["size"] = "small";
                          // } else if (value > 14 && value < 18) {
                          //   styleObj["size"] = "medium";
                          // } else if (value > 18 && value < 32) {
                          //   styleObj["size"] = "large";
                          // } else if (value >= 32) {
                          //   styleObj["size"] = "huge";
                          // } 
                        } else if (key === "horizontalAlign") {
                          if (val) {
                            styleObj["align"] = val;
                          }
                        }
                      })
                    );
                    const obj = {
                      ["attributes"]: styleObj,
                      ["insert"]: content,
                    };
                    delta.ops.push(obj);
                    // console.log(slideDataStructure.textBox);
                  } else if (bulletAttributes) {
                    const styleObj = {};
                    Object.fromEntries(
                      Object.entries(bulletAttributes).filter(([key, val]) => {
                        if (key === "foregroundColor") {
                          if (val !== null) {
                            styleObj["color"] = val;
                          }
                        } else if (key === "fontFamily") {
                          if (val !== null) {
                            styleObj["font"] = val;
                          }
                        } else if (key === "bold") {
                          if (val === true) {
                            styleObj["bold"] = val;
                          }
                        } else if (key === "italic") {
                          if (val === true) {
                            styleObj["italic"] = val;
                          }
                        } else if (key === "fontSize") {
                          if (val) {
                            const { value } = val;
                            let size = Math.ceil(value);
                            if (size % 2 === 1) {
                              size = size - 1;
                            }
                            styleObj["size"] = `${size}px`;
                          }
                        }
                      })
                    );
                    if (content !== "") {
                      const obj = {
                        ["attributes"]: styleObj,
                        ["insert"]: content + " ",
                      };
                      delta.ops.push(obj);
                    }
                  }
                  //   console.log(slideDataStructure);
                }
              }
              
              // Only create the textBox once after processing all paragraphs and spans
              // Ensure it has both delta and styles
              slideDataStructure["textBox"] = {
                ...slideDataStructure["textBox"],
                [textId]: {
                  delta,
                  styles: {
                    position: "absolute",
                    width: boundBox.width,
                    height: boundBox.height,
                    left: position.left,
                    top: position.top,
                    backgroundColor: backgroundColor ? backgroundColor : "",
                    zIndex,
                  },
                },
              };
              //slideDataStructure["textBox"] = textBox;
              // console.log(slideDataStructure);
              // console.log(textBox);
            }
          if (image) {
            const imgId = "image" + v4();
            const width = Number(elementObj.size.width.value / pageWidth) * percentage + '%';
            // console.log(width);
            const height = Number(elementObj.size.height.value / pageHeight) * percentage + '%';
            const left = Number(elementObj.position.x.value / pageWidth) * percentage + '%';
            const top = Number(elementObj.position.y.value / pageHeight) * percentage + '%';
            slideDataStructure.imageIds.push(imgId);
            // console.log(`${slideID}`, image);

            // Handle image data - convert ArrayBuffer or blob URL to base64 if needed
            let imageData = image.contentUrl;
            if (imageData instanceof ArrayBuffer) {
              // Convert ArrayBuffer to base64 data URL
              const uint8Array = new Uint8Array(imageData);
              const binaryString = uint8Array.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
              imageData = btoa(binaryString);
              imageData = `data:image/png;base64,${imageData}`;
            } else if (imageData && imageData.startsWith('blob:')) {
              // Convert blob URL to base64 data URL
              imageData = await convertBlobUrlToBase64(imageData);
              if (!imageData) {
                console.warn(`Failed to convert blob URL for image ${imgId}`);
                return; // Skip this image
              }
            }

            // slideDataStructure["image"] = {};
            slideDataStructure.image[imgId] = {
              ...slideDataStructure["images"],
              ["imageb64"]: imageData,
              styles: {
                position: "absolute",
                width,
                height,
                left,
                top,
                zIndex,
              },
            };
          }
          slideDataStructure = { ...slideDataStructure };
          dataStructure[slideID] = slideDataStructure;
        }
      }

      // Import slides one by one and generate snapshots during import
      // This shows progress to user and generates previews without extra waiting
      dispatch(slideAction.setIsImporting(true));
      dispatch(slideAction.importPresentation()); // Initialize import state

      const slideEntries = Object.entries(dataStructure);
      const generatedMirrorSlides = {};

      for (let i = 0; i < slideEntries.length; i++) {
        const [slideId, slideData] = slideEntries[i];

        // Add the slide to Redux
        dispatch(slideAction.addSlide({ slideId, slideData }));
        dispatch(slideAction.setCurrentSlide(slideId));

        // Wait for slide to render
        await new Promise(resolve => setTimeout(resolve, 700));

        // Generate snapshot while slide is displayed
        try {
          const dataUrl = await getSnapshot();

          // Upload to Cloudinary
          try {
            const cloudinaryUrl = await uploadToCloudinary(dataUrl);
            generatedMirrorSlides[slideId] = cloudinaryUrl;
          } catch (uploadError) {
            console.error(`Failed to upload mirror to Cloudinary for ${slideId}:`, uploadError);
            generatedMirrorSlides[slideId] = dataUrl; // Fallback to base64
          }

          // Update mirror slide in Redux (won't trigger DB save due to isImporting flag)
          dispatch(slideAction.saveMirror({
            dataId: slideId,
            mirrorSlideDataUrl: generatedMirrorSlides[slideId],
          }));

          console.log(`Imported and captured ${slideId} (${i + 1}/${slideEntries.length})`);
        } catch (error) {
          console.error(`Failed to generate snapshot for ${slideId}:`, error);
          generatedMirrorSlides[slideId] = "";
        }
      }

      // Done importing
      dispatch(slideAction.setIsImporting(false));

      // Save everything to database in a single API call
      const importPayload = {
        presentationId: id,
        slideData: {
          slides: dataStructure,
          mirrorSlides: generatedMirrorSlides,
        },
      };
      console.log('Payload size estimate:', JSON.stringify(importPayload).length, 'bytes');
      await slideAPI.saveImportedDataToDb(importPayload);
    }
  };

  const menu = (
    <Menu onClick={handleMenuClick} className="export-menu-item-container">
      <Menu.Item key="import">Import</Menu.Item>
      <Menu.SubMenu key="export" title="Export">
        <Menu.Item key="export.slide">.slide</Menu.Item>
        <Menu.Item key="export.pptx">.pptx (PowerPoint)</Menu.Item>
        <Menu.Item key="export.pdf">.pdf (PDF)</Menu.Item>
      </Menu.SubMenu>
    </Menu>
  );

  const handleFileNameChange = (e) => {
    setFileName(e.target.value)
  }

  return (
    <>
      <Tooltip
        overlayInnerStyle={{
          width: "auto",
          minHeight: "20px",
          fontSize: "10px",
          padding: "5px",
          textAlign: "center",
          backgroundColor: "black",
          lineHeight: "10px",
          display: `${isDropdownVisible ? "none" : "flex"}`,
        }}
        placement="bottom"
        title={"Import/Export"}
      >
        <Dropdown
          overlay={menu}
          trigger={["click"]}
          onVisibleChange={(visible) => setIsDropdownVisible(visible)}
        >
          <button className={classes["toolbar-btn"]}>
            <img src={importExportIcon} width={24} alt="Import/Export" />
          </button>
        </Dropdown>
      </Tooltip>
      <input
        type="file"
        accept={[".slide", ".pptx"]}
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={handleImport}
      />
      <Modal
        title={
          exportType === "pptx" ? "Export to PowerPoint" :
            exportType === "pdf" ? "Export to PDF" :
              "Export Presentation"
        }
        visible={isModalVisible}
        onOk={handleExport}
        onCancel={() => !isExporting && setIsModalVisible(false)}
        afterClose={() => form.resetFields()}
        okText={isExporting ? "Exporting..." :
          exportType === "pptx" ? "Download" :
            exportType === "pdf" ? "Download" :
              "Export"
        }
        cancelText="Cancel"
        confirmLoading={isExporting}
        okButtonProps={{ disabled: isExporting, className: 'export-modal-btn-download' }}
        cancelButtonProps={{ disabled: isExporting, className: 'export-modal-btn-cancel' }}
        closable={!isExporting}
        maskClosable={!isExporting}
        className="export-document-modal"
        centered
      >
        <Spin
          spinning={isExporting}
          tip={
            exportType === "pptx" ? "Generating PowerPoint file..." :
              exportType === "pdf" ? "Generating PDF file..." :
                "Preparing export package..."
          }
        >
          <Form form={form} layout="vertical">
            <Form.Item
              label="File Name"
              name="fileName"
              rules={[
                {
                  required: true,
                  message: "Please enter a file name.",
                },
                {
                  pattern: /^[^<>:;,?"*|/\\]+$/,
                  message: "File name contains invalid characters.",
                },
              ]}
            >
              <Input
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name"
                disabled={isExporting}
                className="export-modal-input"
              />
            </Form.Item>
            {exportType && (
              <div className="export-format-info">
                <div className="export-format-label">
                  Export format: <strong>.{exportType}</strong>
                </div>
                {exportType === "pptx" ? (
                  <>
                    <div className="export-format-description">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Preserves formatting, images, and layout fidelity
                    </div>
                    <div className="export-format-description">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Fully editable across PowerPoint-compatible apps
                    </div>
                    {slides && (
                      <div className="export-format-meta">
                        {(() => {
                          const preview = getExportPreview(slides);
                          return `${preview.slideCount} slide${preview.slideCount !== 1 ? 's' : ''} will be exported`;
                        })()}
                      </div>
                    )}
                  </>
                ) : exportType === "pdf" ? (
                  <>
                    <div className="export-format-description">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      High-quality PDF with exact visual representation
                    </div>
                    <div className="export-format-description">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Perfect for sharing, printing, or archiving presentations
                    </div>
                    {slides && (
                      <div className="export-format-meta">
                        {(() => {
                          const preview = getExportPreview(slides);
                          return `${preview.slideCount} slide${preview.slideCount !== 1 ? 's' : ''} will be exported`;
                        })()}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="export-format-description">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Native MileSlide format with layouts and metadata
                    </div>
                    <div className="export-format-description">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Ideal for backups or collaboration with teammates
                    </div>
                  </>
                )}
              </div>
            )}
          </Form>
        </Spin>
      </Modal>
    </>
  );
};

export default ImportExportMenu;
